import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  model,
  OnInit,
  output,
  Signal,
  signal,
  TemplateRef,
  viewChild,
  WritableSignal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  MbsPopupType,
  MbsSize,
  ModalService,
  ModalSettings,
  SwitcherComponent,
  TagComponent
} from 'mbs-ui-kit';
import { GuidEmpty } from 'mbs-ui-shared@constants';
import { RemoteDeployDataFacade } from 'mbs-ui-shared@facades/remote-deploy-data.facade';
import { Company, StorageType } from 'mbs-ui-shared@models';
import { CompanyDestination } from 'mbs-ui-shared@models/Company';
import { ApplyToEnum } from 'mbs-ui-shared@models/remote-deploy/apply-to-enum';
import { ApplyToModel } from 'mbs-ui-shared@models/remote-deploy/apply-to-model';
import { RdConfigOsTypeEnum } from 'mbs-ui-shared@models/remote-deploy/rd-config-os-type.enum';
import { RemoteDeployConfiguration } from 'mbs-ui-shared@models/remote-deploy/remote-deploy-configuration';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { mediumDateWithTimeMoment } from 'mbs-ui-shared@utils/date';
import { StorageTypeIconPipe } from 'mbs-ui-shared@utils/pipes/storage-type-icon.pipe';
import moment from 'moment';
import { catchError, EMPTY, forkJoin, from, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { BackupTargetVolumesEnum } from 'wizards/models/what-backup-models';
import { CompanyWizardFacade } from 'wizards/services/entity-wizards/company/company-wizard.facade';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import {
  BackupDestinationSettingsModalComponent,
  BackupDestinationSettingsModalResult
} from '../../modals/backup-destination-settings-modal/backup-destination-settings-modal.component';
import {
  CompressionEncryptionModalComponent,
  CompressionEncryptionModalResult
} from '../../modals/compression-encryption-modal/compression-encryption-modal.component';
import {
  ScheduleOptionsModalComponent,
  ScheduleOptionsModalResult
} from '../../modals/schedule-options-modal/schedule-options-modal.component';
import { WhatBackupModalComponent, WhatBackupModalResult } from '../../modals/what-backup-modal/what-backup-modal.component';
import {
  CompanyBackupTemplate,
  CompanyBackupTemplateDefinition,
  CompanyBackupTemplateDefinitions,
  CompanyBackupTemplateField,
  CompanyBackupTemplatesStepDefaultValue,
  CompanyBackupTemplatesStepValue,
  getTemplatePlanDefaults,
  getTemplatePlanType
} from '../../models/company-wizard-models';
import { templatesToConfigurations } from '../../utils/company-wizard-configurations.util';
import {
  companyStorageConnections,
  storageAccountByBucket,
  templateRetentionForSchedule,
  templateSupportsFFI,
  toLegacyStorageAccounts
} from '../../utils/company-wizard-plan-context.util';
import { TemplateProblem, templateProblems } from '../../utils/company-wizard-template-validation.util';

const BackupVolumesTextKeys: Partial<Record<BackupTargetVolumesEnum, string>> = {
  [BackupTargetVolumesEnum.SystemRequired]: 'wizards:system_partitions',
  [BackupTargetVolumesEnum.AllDrives]: 'wizards:all_drives',
  [BackupTargetVolumesEnum.FixedOnly]: 'wizards:selected_fixed',
  [BackupTargetVolumesEnum.SelectedOnly]: 'wizards:selected_partitions'
};

const TemplatesOrder = ['files-windows', 'image-windows', 'files-linux', 'files-mac'];

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    ButtonComponent,
    FormsModule,
    I18NextPipe,
    NgbTooltipModule,
    StorageTypeIconPipe,
    SwitcherComponent,
    TagComponent
  ],
  selector: 'mbs-company-backup-templates-step',
  standalone: true,
  styleUrls: ['./company-backup-templates-step.component.scss'],
  templateUrl: './company-backup-templates-step.component.html'
})
export class CompanyBackupTemplatesStepComponent implements OnInit {
  private readonly companyWizardFacade = inject(CompanyWizardFacade);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly mainService = inject(RemoteManagementWizardsService);
  private readonly modalService = inject(ModalService);
  private readonly rdFacade = inject(RemoteDeployDataFacade);

  public readonly value = model<CompanyBackupTemplatesStepValue>(CompanyBackupTemplatesStepDefaultValue);
  public readonly goToDestinations = output<void>();

  public readonly alertType = MbsPopupType;
  public readonly nextStep = output<boolean>();
  public readonly valid: Signal<boolean> = computed(() => this.appliedSnapshot() === this.snapshot());
  public readonly sortedTemplates: Signal<CompanyBackupTemplate[]> = computed(() =>
    [...this.value().templates].sort((a, b) => TemplatesOrder.indexOf(a.id) - TemplatesOrder.indexOf(b.id))
  );
  public readonly expandedTemplateId = signal<string>(null);
  // A template can only be created against one known destination, and picking between several is not wired yet
  public readonly destination: Signal<CompanyDestination> = computed(() =>
    this.destinations().length === 1 ? this.destinations()[0] : null
  );
  public readonly hasManyDestinations = computed(() => this.destinations().length > 1);
  public readonly hasOnlyAzureVmDestinations = computed(
    () => !this.destinations().length && !!this.companyWizardFacade.company()?.inheritedDestinations?.length
  );
  public readonly availableStorages = toSignal(this.rdFacade.SAWithDestinations$, { initialValue: [] });

  protected readonly templatesToConfigurations = templatesToConfigurations;

  private readonly createdConfigurations: WritableSignal<RemoteDeployConfiguration[]> = signal([]);
  private readonly appliedSnapshot: WritableSignal<string> = signal(null);
  private readonly destinations: Signal<CompanyDestination[]> = computed(() =>
    (this.companyWizardFacade.company()?.inheritedDestinations ?? []).filter(
      ({ storageAccountType }) => storageAccountType !== StorageType.AzureVM
    )
  );
  private readonly ffiResetIds: WritableSignal<ReadonlySet<string>> = signal(new Set<string>());
  public readonly ffiResetPlanNames: Signal<string[]> = computed(() =>
    this.value()
      .templates.filter((template) => this.ffiResetIds().has(template.id) && template.create && !!this.templateDestination(template))
      .map(({ name }) => name)
  );
  private readonly enabledTemplates: Signal<CompanyBackupTemplate[]> = computed(() =>
    this.value()
      .templates.filter((template) => template.create && !!this.templateDestination(template))
      .map((template) => this.withResolvedDestination(template))
  );
  private readonly snapshot: Signal<string> = computed(() => JSON.stringify(this.enabledTemplates()));
  public readonly templateProblems: Signal<ReadonlyMap<string, TemplateProblem[]>> = computed(
    () =>
      new Map(
        this.enabledTemplates()
          .map((template) => [template.id, templateProblems(template, this.templateConnection(template))] as const)
          .filter(([, problems]) => problems.length)
      )
  );
  public readonly invalidTemplates: Signal<CompanyBackupTemplate[]> = computed(() =>
    this.enabledTemplates().filter(({ id }) => this.templateProblems().has(id))
  );

  private readonly invalidTemplatesBody = viewChild<TemplateRef<unknown>>('invalidTemplatesBody');

  constructor() {
    effect(() => this.resetUnsupportedSchedules());
    effect(() => this.loadAvailableStorages(this.companyWizardFacade.company()));
  }

  ngOnInit(): void {
    if (this.value().templates.length) return;

    this.value.set({ templates: CompanyBackupTemplateDefinitions.map((definition) => this.buildTemplate(definition)) });
  }

  public forceValid(): void {
    if (this.valid()) return;

    if (!this.hasUnsavedChanges()) return this.finishStep();

    if (this.invalidTemplates().length) return void this.showInvalidTemplates();

    from(this.confirmApply())
      .pipe(
        switchMap((confirmed) => (confirmed ? this.applyChanges() : EMPTY)),
        catchError(() => EMPTY),
        take(1),
        untilDestroyed(this)
      )
      .subscribe(() => this.finishStep());
  }

  public hasUnsavedChanges(): boolean {
    if (this.valid()) return false;

    return !!this.enabledTemplates().length || !!this.createdConfigurations().length;
  }

  public confirmApply(): Promise<boolean> {
    const key = this.createdConfigurations().length ? 'confirmUpdate' : 'confirmCreate';
    const settings: ModalSettings = {
      header: { title: this.getStepText(`${key}.title`) },
      footer: {
        okButton: { text: this.i18nPipe.transform('shared:buttons.ok') },
        cancelButton: { text: this.i18nPipe.transform('shared:buttons.cancel') }
      }
    };

    return this.modalService.open(settings, this.getStepText(`${key}.message`));
  }

  public showInvalidTemplates(): Promise<void> {
    const settings: ModalSettings = {
      header: { title: this.getStepText('invalidTemplates.title') },
      footer: { okButton: { text: this.i18nPipe.transform('shared:buttons.ok') }, cancelButton: { show: false } }
    };

    this.expandedTemplateId.set(this.invalidTemplates()[0]?.id ?? null);

    return this.modalService
      .open(settings, this.invalidTemplatesBody())
      .then(() => null)
      .catch(() => null);
  }

  public applyChanges(): Observable<RemoteDeployConfiguration[]> {
    const company = this.companyWizardFacade.company();
    const created = this.createdConfigurations();
    const configurations = this.templatesToConfigurations(this.enabledTemplates(), {
      company,
      mainService: this.mainService,
      nameFor: (osType) => this.configurationName(company.name, osType),
      storageAccountIdFor: (bucketId) => storageAccountByBucket(this.availableStorages(), bucketId)?.id,
      storageAccounts: toLegacyStorageAccounts(this.availableStorages()),
      storageConnectionFor: (template) => this.templateConnection(template)
    });

    const requests = configurations.map((configuration) => {
      const existing = created.find(({ osType }) => osType === configuration.osType);

      if (!existing) return this.rdFacade.add(configuration).pipe(map((result) => ({ ...configuration, id: result?.id })));

      const payload = { ...configuration, id: existing.id, name: existing.name };

      return this.rdFacade.update(payload).pipe(map(() => payload));
    });

    // Configurations are never deleted from here: only their plans are, and an emptied one is switched off instead
    const emptied = created
      .filter(({ osType }) => !configurations.some((configuration) => configuration.osType === osType))
      .map((configuration) => {
        const payload = { ...configuration, enabled: false, plans: [] } as RemoteDeployConfiguration;

        return this.rdFacade.update(payload).pipe(map(() => payload));
      });

    this.companyWizardFacade.loading.set(true);

    return forkJoin([...requests, ...emptied]).pipe(
      take(1),
      tap({
        next: (result) => {
          this.createdConfigurations.set(result);
          this.appliedSnapshot.set(this.snapshot());
          this.companyWizardFacade.loading.set(false);
        },
        error: () => this.companyWizardFacade.loading.set(false)
      })
    );
  }

  public editDestinationClickHandler(template: CompanyBackupTemplate): void {
    if (!this.templateDestinations(template).length) return this.showDestinationRequired();

    this.openDestinationSettings(template).subscribe();
  }

  // A template keeps its own destination once it is picked; until then it follows the company's single one
  public templateDestination(template: CompanyBackupTemplate): CompanyDestination {
    const connectionId = template.destinationPlusRetention?.whereBackupStep?.ConnectionID;
    const destinations = this.templateDestinations(template);
    const companyDestination = destinations.includes(this.destination()) ? this.destination() : null;

    if (!connectionId || connectionId === GuidEmpty) return companyDestination;

    return destinations.find(({ bucketId }) => bucketId === connectionId) ?? companyDestination;
  }

  public editTemplateFieldClickHandler(template: CompanyBackupTemplate, field: CompanyBackupTemplateField): void {
    if (field === 'compression') return this.toggleCompression(template);

    if (field === 'encryption') return this.editCompressionEncryption(template);

    if (field === 'schedule') return void (this.scheduleEditable(template) && this.editScheduleOptions(template));

    from(this.modalService.openCustom<WhatBackupModalResult>(WhatBackupModalComponent, { data: { template }, size: MbsSize.md }))
      .pipe(
        catchError(() => of(null)),
        take(1),
        untilDestroyed(this)
      )
      .subscribe((result) => {
        if (!result) return;

        this.value.update((value) => ({
          ...value,
          templates: value.templates.map((item) =>
            item.id === template.id
              ? { ...item, whatBackupStep: result.whatBackupStep, whatBackupTreeStep: result.whatBackupTreeStep }
              : item
          )
        }));
      });
  }

  // Falls back to the template's default text until the source has actually been edited
  public scheduleEditable(template: CompanyBackupTemplate): boolean {
    return !!this.templateDestination(template);
  }

  public templateSource(template: CompanyBackupTemplate): string {
    const includeFolders = template.whatBackupTreeStep?.includeFolders;

    if (includeFolders?.length) return includeFolders.join(', ');

    const volumes = template.whatBackupStep?.BackupVolumes;

    if (volumes) return this.i18nPipe.transform(BackupVolumesTextKeys[volumes]);

    return template.source;
  }

  public toggleTemplateHandler(template: CompanyBackupTemplate): void {
    this.expandedTemplateId.set(this.expandedTemplateId() === template.id ? null : template.id);
  }

  // The kit switcher lets us veto the flip, so the toggle never moves instead of moving and snapping back
  public beforeCreateChangeHandler(template: CompanyBackupTemplate, event: { stop: () => void }): void {
    if (this.templateDestination(template)) return;

    event.stop();

    if (!this.templateDestinations(template).length) return this.showDestinationRequired();

    this.expandedTemplateId.set(template.id);
    this.selectDestinationAndEnable(template);
  }

  public createChangeHandler(template: CompanyBackupTemplate, create: boolean): void {
    if (!create) this.dropFfiReset(template.id);

    this.value.update((value) => ({
      ...value,
      templates: value.templates.map((item) => (item.id === template.id ? { ...item, create } : item))
    }));
  }

  // Private section
  // Compression is a plain switch on the card; the same flag is the top checkbox of the encryption modal
  private toggleCompression(template: CompanyBackupTemplate): void {
    const step = template.compressionEncryptionStep;

    this.patchTemplate(template, { compressionEncryptionStep: { ...step, UseCompression: !step.UseCompression } });
  }

  private editScheduleOptions(template: CompanyBackupTemplate): void {
    // Modals look the storage up by the template's own ConnectionID, which a template following the company's only destination lacks
    const data = {
      companyDestinations: this.templateDestinations(template),
      availableStorages: this.availableStorages(),
      template: this.withResolvedDestination(template)
    };

    from(this.modalService.openCustom<ScheduleOptionsModalResult>(ScheduleOptionsModalComponent, { data, size: MbsSize.md }))
      .pipe(
        catchError(() => of(null)),
        take(1),
        untilDestroyed(this)
      )
      .subscribe((result) => {
        if (!result) return;

        this.dropFfiReset(template.id);
        this.patchTemplate(template, { ...result, ...templateRetentionForSchedule(template, result.scheduleStep) });
      });
  }

  private editCompressionEncryption(template: CompanyBackupTemplate): void {
    from(
      this.modalService.openCustom<CompressionEncryptionModalResult>(CompressionEncryptionModalComponent, {
        data: { template },
        size: MbsSize.md
      })
    )
      .pipe(
        catchError(() => of(null)),
        take(1),
        untilDestroyed(this)
      )
      .subscribe((result) => result && this.patchTemplate(template, { compressionEncryptionStep: result }));
  }

  private withResolvedDestination(template: CompanyBackupTemplate): CompanyBackupTemplate {
    const step = template.destinationPlusRetention;
    const connectionId = step?.whereBackupStep?.ConnectionID;

    if (connectionId && connectionId !== GuidEmpty) return template;

    return {
      ...template,
      destinationPlusRetention: {
        ...step,
        whereBackupStep: { ...step.whereBackupStep, ConnectionID: this.templateDestination(template).bucketId }
      }
    };
  }

  private resetUnsupportedSchedules(): void {
    const templates = this.value().templates;
    const updated = templates.map((template) => this.withSupportedSchedule(template));
    const resetIds = updated.filter((template, index) => template !== templates[index]).map(({ id }) => id);

    if (!resetIds.length) return;

    this.value.update((value) => ({ ...value, templates: updated }));
    this.ffiResetIds.update((ids) => new Set([...ids, ...resetIds]));
  }

  private dropFfiReset(templateId: string): void {
    if (!this.ffiResetIds().has(templateId)) return;

    this.ffiResetIds.update((ids) => new Set([...ids].filter((id) => id !== templateId)));
  }

  // A destination or advanced option picked after FFI can rule it out, and the plan wizards drop FFI in that case too;
  // without a destination there is nothing to judge yet, so the operator's choice stays
  private withSupportedSchedule(template: CompanyBackupTemplate): CompanyBackupTemplate {
    if (template.scheduleStep.ScheduleType !== 'ffi') return template;

    const connection = this.templateConnection(template);

    return connection && !templateSupportsFFI(template, connection) ? this.withNoSchedule(template) : template;
  }

  private withNoSchedule(template: CompanyBackupTemplate): CompanyBackupTemplate {
    return {
      ...template,
      schedule: this.i18nPipe.transform('wizards:schedule.defaultMode.noSchedule'),
      scheduleStep: { ...template.scheduleStep, ScheduleType: 'noschedule' }
    };
  }

  private templateConnection(template: CompanyBackupTemplate): StorageConnection {
    const destination = this.templateDestination(template);

    if (!destination) return null;

    return companyStorageConnections([destination], this.availableStorages())[0] ?? null;
  }

  private loadAvailableStorages(company: Company): void {
    if (!company) return;

    this.rdFacade.getDestinations(
      { ...new ApplyToModel(), applyTo: ApplyToEnum.ApplyToAllSelectedCompanies, selectedCompanies: [company.id] },
      null
    );
  }

  private finishStep(): void {
    this.appliedSnapshot.set(this.snapshot());
    this.nextStep.emit(false);
  }

  private configurationName(companyName: string, osType: RdConfigOsTypeEnum): string {
    return this.i18nPipe.transform('wizards:entityWizards.company.templatesStep.configurationName', {
      company: companyName,
      date: moment().format(mediumDateWithTimeMoment),
      os: RdConfigOsTypeEnum[osType]
    });
  }

  private patchTemplate(template: CompanyBackupTemplate, patch: Partial<CompanyBackupTemplate>): void {
    this.value.update((value) => ({
      ...value,
      templates: value.templates.map((item) => (item.id === template.id ? { ...item, ...patch } : item))
    }));
  }

  private buildTemplate(definition: CompanyBackupTemplateDefinition): CompanyBackupTemplate {
    const defaults = getTemplatePlanDefaults(definition);
    const source = this.getText(definition.id, 'source');

    return {
      ...definition,
      ...defaults,
      applyTo: this.getText(definition.id, 'applyTo'),
      create: false,
      name: this.getText(definition.id, 'name'),
      planType: getTemplatePlanType(definition.isImageBased),
      schedule: this.i18nPipe.transform('wizards:schedule.defaultMode.noSchedule'),
      source,
      // The default source text is the template's starting plan value, not just a caption
      whatBackupTreeStep: defaults.whatBackupTreeStep && {
        ...defaults.whatBackupTreeStep,
        includeFolders: source.split(',').map((path) => path.trim())
      }
    };
  }

  private openDestinationSettings(template: CompanyBackupTemplate): Observable<BackupDestinationSettingsModalResult> {
    const data = {
      companyDestinations: this.templateDestinations(template),
      availableStorages: this.availableStorages(),
      template: this.templateDestination(template) ? this.withResolvedDestination(template) : template
    };

    return from(
      this.modalService.openCustom<BackupDestinationSettingsModalResult>(BackupDestinationSettingsModalComponent, {
        data,
        size: MbsSize.md
      })
    ).pipe(
      catchError(() => of(null)),
      take(1),
      untilDestroyed(this),
      tap((result) => result && this.applyDestinationSettings(template, result))
    );
  }

  private applyDestinationSettings(template: CompanyBackupTemplate, result: BackupDestinationSettingsModalResult): void {
    const { advancedOptions, destinationPlusRetention, schedule, scheduleStep } = result;

    if (scheduleStep) this.dropFfiReset(template.id);

    this.patchTemplate(template, { advancedOptions, destinationPlusRetention, ...(scheduleStep && { schedule, scheduleStep }) });
  }

  private showDestinationRequired(): void {
    this.modalService
      .confirm({ header: { title: this.getModalText('title') } }, this.getModalText(this.destinationRequiredTextKey()))
      .then((confirmed) => confirmed && this.goToDestinations.emit())
      .catch(() => null);
  }

  private selectDestinationAndEnable(template: CompanyBackupTemplate): void {
    this.openDestinationSettings(template).subscribe((result) => {
      const connectionId = result?.destinationPlusRetention?.whereBackupStep?.ConnectionID;

      if (!connectionId || connectionId === GuidEmpty) return;

      this.patchTemplate(template, { create: true });
    });
  }

  private getStepText(key: string): string {
    return this.i18nPipe.transform(`wizards:entityWizards.company.templatesStep.${key}`);
  }

  private getText(id: string, key: string): string {
    return this.i18nPipe.transform(`wizards:entityWizards.company.templatesStep.defaults.${id}.${key}`);
  }

  private templateDestinations(template: CompanyBackupTemplate): CompanyDestination[] {
    if (!template.isUnix) return this.destinations();

    return this.destinations().filter(({ storageAccountType }) => storageAccountType !== StorageType.LocalFS);
  }

  private destinationRequiredTextKey(): string {
    if (this.destinations().length) return 'unixLocalFsText';

    return this.hasOnlyAzureVmDestinations() ? 'azureVmText' : 'text';
  }

  private getModalText(key: string): string {
    return this.i18nPipe.transform(`wizards:entityWizards.company.templatesStep.destinationRequired.${key}`);
  }
}
